import type { AuctisyncSupabase } from './client.js';

/**
 * Module #23 chunk 8 — a bidder's smart alerts on a single lot.
 *
 * Two rule types, both per-lot:
 *
 *  - `price_below` — the BARGAIN alert. Fires once when the lot enters its
 *    final window (the bidder picks 15/30/60 minutes) while the current bid is
 *    still under their threshold. Evaluated by the `notify_bargain_alerts`
 *    pg_cron, not on every bid. Because price only rises in an ascending
 *    auction, this means "still a deal near close".
 *  - `reserve_met` — fires when the lot's `reserve_met` flips true.
 *
 * WHY THIS IS SHARED. The website has had these since May; the app had none, so
 * a bidder who lives on their phone could not be told a lot was closing cheap.
 * The rules below are not obvious, and one of them is a genuine footgun (see
 * the re-arm note on `saveLotAlerts`). Reimplementing them natively from the
 * website's JSX would have meant a second chance to get that wrong.
 *
 * Cross-tenant rule types (new_in_category and friends) stay out of scope, as
 * does an alerts-management screen — v1 sets alerts on the lot they concern.
 */

/** The windows a bidder may choose, in minutes. */
export const ALERT_WINDOW_CHOICES = [15, 30, 60] as const;

/**
 * Matches the platform's existing "closing soon" notification window, so a
 * bidder who takes the default gets an alert on the same rhythm as everything
 * else the product already sends them.
 */
export const DEFAULT_ALERT_WINDOW_MINUTES = 30;

/** A bidder's current alert configuration for one lot. */
export interface LotAlertState {
  /** Null when no bargain alert is set. */
  priceBelowThresholdCents: number | null;
  /** Null when no bargain alert is set. */
  priceBelowWindowMinutes: number | null;
  reserveMetActive: boolean;
}

export const NO_ALERTS: LotAlertState = {
  priceBelowThresholdCents: null,
  priceBelowWindowMinutes: null,
  reserveMetActive: false,
};

export type FetchAlertsResult =
  | { ok: true; alerts: LotAlertState }
  | { ok: false; message: string };

interface AlertRow {
  rule_type?: unknown;
  rule_params?: unknown;
}

function num(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

export async function fetchLotAlerts(
  supabase: AuctisyncSupabase,
  params: { userId: string; lotId: string },
): Promise<FetchAlertsResult> {
  const { data, error } = await supabase
    .from('bidder_alerts')
    .select('rule_type, rule_params, active')
    .eq('user_id', params.userId)
    .eq('target_lot_id', params.lotId)
    .eq('active', true);

  if (error) return { ok: false, message: error.message };

  const alerts: LotAlertState = { ...NO_ALERTS };

  for (const raw of data ?? []) {
    const row = raw as AlertRow;
    if (row.rule_type === 'price_below') {
      const p = (row.rule_params ?? {}) as { threshold_cents?: unknown; window_minutes?: unknown };
      alerts.priceBelowThresholdCents = num(p.threshold_cents);
      // An older row saved before the window was configurable has no
      // window_minutes. It was created under the 30-minute behaviour, so that
      // is what it still means — treating it as "unset" would silently change
      // when an existing alert fires.
      alerts.priceBelowWindowMinutes = num(p.window_minutes) ?? DEFAULT_ALERT_WINDOW_MINUTES;
    } else if (row.rule_type === 'reserve_met') {
      alerts.reserveMetActive = true;
    }
  }

  return { ok: true, alerts };
}

export interface SaveLotAlertsInput {
  userId: string;
  lotId: string;
  /** Whether the lot has a reserve at all — no reserve, no reserve alert. */
  hasReserve: boolean;
  /** Desired bargain threshold, or null to clear it. */
  thresholdCents: number | null;
  windowMinutes: number;
  reserveMetEnabled: boolean;
  /** What is currently stored. Drives the diff — see the re-arm note. */
  existing: LotAlertState;
}

export type SaveAlertsResult = { ok: true } | { ok: false; message: string };

/**
 * Apply the bidder's choices as a DIFF against what is stored.
 *
 * 🔴 THE RULE THAT MAKES THIS NON-OBVIOUS. `price_below` fires once, tracked by
 * `last_triggered_at`. Deleting and re-inserting the row resets that to NULL,
 * which RE-ARMS an alert that has already fired. So an incidental save — the
 * bidder toggling the reserve alert, or pressing Save with nothing changed —
 * must NOT touch an unchanged bargain alert, or they get the same notification
 * a second time. The row is therefore rewritten only when its trigger
 * parameters actually differ.
 *
 * This is why the caller must pass `existing` rather than letting this function
 * re-read: the comparison has to be against what the bidder was shown, not
 * against a row that may have changed underneath them.
 */
export async function saveLotAlerts(
  supabase: AuctisyncSupabase,
  input: SaveLotAlertsInput,
): Promise<SaveAlertsResult> {
  const { userId, lotId, existing } = input;

  const clearPriceAlert = async () => {
    const { error } = await supabase
      .from('bidder_alerts')
      .delete()
      .eq('user_id', userId)
      .eq('target_lot_id', lotId)
      .eq('rule_type', 'price_below');
    return error;
  };

  try {
    const wants = input.thresholdCents !== null && input.thresholdCents > 0;

    if (wants) {
      const changed =
        existing.priceBelowThresholdCents !== input.thresholdCents ||
        existing.priceBelowWindowMinutes !== input.windowMinutes;

      // See the re-arm note above. Not an optimisation — a correctness rule.
      if (changed) {
        const delErr = await clearPriceAlert();
        if (delErr) return { ok: false, message: delErr.message };

        const { error } = await supabase.from('bidder_alerts').insert({
          user_id: userId,
          rule_type: 'price_below',
          target_lot_id: lotId,
          rule_params: {
            threshold_cents: input.thresholdCents,
            window_minutes: input.windowMinutes,
          },
          active: true,
        });
        if (error) return { ok: false, message: error.message };
      }
    } else if (existing.priceBelowThresholdCents !== null) {
      const delErr = await clearPriceAlert();
      if (delErr) return { ok: false, message: delErr.message };
    }

    // A lot with no reserve has nothing to alert on; touching the row anyway
    // would create an alert that can never fire.
    if (input.hasReserve) {
      if (input.reserveMetEnabled && !existing.reserveMetActive) {
        const { error } = await supabase.from('bidder_alerts').insert({
          user_id: userId,
          rule_type: 'reserve_met',
          target_lot_id: lotId,
          rule_params: {},
          active: true,
        });
        if (error) return { ok: false, message: error.message };
      } else if (!input.reserveMetEnabled && existing.reserveMetActive) {
        const { error } = await supabase
          .from('bidder_alerts')
          .delete()
          .eq('user_id', userId)
          .eq('target_lot_id', lotId)
          .eq('rule_type', 'reserve_met');
        if (error) return { ok: false, message: error.message };
      }
    }

    return { ok: true };
  } catch (err) {
    return { ok: false, message: err instanceof Error ? err.message : String(err) };
  }
}
